import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Menu, MenuItem } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { downloadRequestItemsXlsx } from '../../../api/requests/index.js';
import type { TransitionAction } from '../role.js';
import type { RequestView } from '../types.js';
import { triggerBrowserDownload } from './requestDrawerDownload.js';

export interface RussiaRateButtonProps {
  visible: boolean;
  disabled: boolean;
  pending: boolean;
  tooltip: string;
  onClick: () => void;
}

// WHY (#2119): the Duplicate action (label `requests:actions.duplicate`).
//   Owner-only visibility is decided by the caller; the menu item only needs
//   the in-flight flag (single-flight disable) + click handler.
export interface DuplicateButtonProps {
  visible: boolean;
  pending: boolean;
  onClick: () => void;
}

// WHY (#2200 D-4): float the (single) primary transition to the front with a
//   STABLE sort so it heads the «⋮» menu; the remaining transitions follow in
//   their matrix order.
const rank = (a: TransitionAction): number => (a.kind === 'primary' ? 0 : 1);

/**
 * @aiPurpose desktop/narrow header action cluster for the China request
 *   drawer — a single «⋮» menu (#2200) holding every business action, plus
 *   the standalone Excel-export and Close icon buttons.
 * @aiInvariant all China business actions live inside `ui/Menu` as `MenuItem`s
 *   (`role="menuitem"`): on desktop (`!narrow`) the primary-first transition
 *   set, then russiaRate, then duplicate; on narrow only russiaRate + duplicate
 *   (transitions live in the `RequestDrawerStickyActionBar`). The «⋮» renders
 *   only when at least one business action is visible — never an empty menu.
 *   Only export + close render outside the menu.
 */
export function RequestDrawerHeaderActions(props: {
  r: RequestView;
  actions: TransitionAction[];
  narrow: boolean;
  onAction: (a: TransitionAction) => void;
  onClose: () => void;
  russiaRate: RussiaRateButtonProps;
  duplicate: DuplicateButtonProps;
}) {
  const { t } = useTranslation(['requests', 'common']);
  const { r, actions, narrow, onAction, onClose, russiaRate, duplicate } = props;
  const showToast = useToastStore((s) => s.show);
  const [exporting, setExporting] = useState(false);

  const onExportClick = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { blob, filename } = await downloadRequestItemsXlsx(r.id);
      // INVARIANT: filename comes from server `Content-Disposition` ASCII
      //   `filename="..."`; if the parser misses it we fall back to the
      //   deterministic `LR-NNNNN.xlsx` shape the backend always emits.
      triggerBrowserDownload(blob, filename ?? `${r.id}.xlsx`);
    } catch {
      showToast({ tone: 'error', text: t('requests:export.error') });
    } finally {
      setExporting(false);
    }
  };

  // D-5: transitions collapse into the «⋮» on desktop (primary-first); on
  //   narrow they are excluded — the sticky action bar owns them there.
  const orderedTransitions = narrow ? [] : [...actions].sort((a, b) => rank(a) - rank(b));
  const hasBusinessActions =
    orderedTransitions.length > 0 || russiaRate.visible || duplicate.visible;

  return (
    <div
      style={{
        display: 'flex',
        gap: 6,
        flex: 'none',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
      }}
    >
      {hasBusinessActions && (
        <Menu
          trigger={(triggerProps) => (
            <IconButton
              {...triggerProps}
              icon="more-vertical"
              label={t('common:actions.more')}
              data-testid="request-drawer-actions-more"
            />
          )}
        >
          {orderedTransitions.map((a) => {
            const label = t(`requests:${a.labelKey}`);
            const tooltip = a.disabledReasonKey
              ? (t(`requests:${a.disabledReasonKey}`) as string)
              : undefined;
            // D-10/D-11: transitions are `MenuItem`s (role="menuitem"), never
            //   raw Buttons — Menu close/keyboard nav depends on the role. A
            //   disabled-with-reason item carries the tooltip on an inner
            //   `<span title>` so it does not break the menuitem role/name.
            return (
              <MenuItem key={a.key} disabled={a.disabled === true} onClick={() => onAction(a)}>
                {tooltip ? <span title={tooltip}>{label}</span> : label}
              </MenuItem>
            );
          })}
          {russiaRate.visible && (
            // D-11: native `disabled` suppresses hover on the button itself, so
            //   the empty-cargo / pending / error reason rides an inner
            //   `<span title>` inside the menu item instead.
            <MenuItem
              disabled={russiaRate.disabled || russiaRate.pending}
              onClick={russiaRate.onClick}
            >
              <span title={russiaRate.tooltip}>{t('requests:actions.russiaRate.button')}</span>
            </MenuItem>
          )}
          {duplicate.visible && (
            <MenuItem disabled={duplicate.pending} onClick={duplicate.onClick}>
              {t('requests:actions.duplicate')}
            </MenuItem>
          )}
        </Menu>
      )}
      <IconButton
        icon={exporting ? 'loader' : 'download'}
        label={exporting ? t('requests:export.downloading') : t('requests:export.action')}
        onClick={onExportClick}
        disabled={exporting}
      />
      <IconButton icon="x" label={t('common:actions.close')} onClick={onClose} />
    </div>
  );
}
